import { useMutation } from "@apollo/client/react";
import Nango from "@nangohq/frontend";
import _ from "lodash";
import { useState } from "react";
import { BriefcaseIcon } from "lucide-react";
import { GoogleIcon } from "@/objects/@icons";

import {
  EmailConnectionStatusEnum,
  ExternalGrantStatusEnum,
  PermissionEnum,
  BulkOperationDestinationEnum,
  BulkOperationEnum,
  type ExternalClientsQuery,
} from "@/__generated__/graphql";
import {
  SettingsViewCard,
  SettingsViewLayout,
} from "@/app/app/_/views/SettingsView/SettingsViewLayout";
import { describePermissions } from "@/components/organisms/Roles/Roles.tools";
import {
  useOpenClientBooks,
  useRevokeExternalGrant,
} from "@/components/organisms/ExternalGrants/ExternalGrants.hooks";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import {
  CONNECTION_VIEWS,
  connectionVariables,
  type ConnectionViewConfig,
} from "@/hooks/useConnectionSetup";
import { useScope } from "@/hooks/useScope";
import { Objects } from "@/objects";
import { toColumns } from "@/objects/@utils";
import { State } from "@/state";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

/** Nango's integration for Google Sheets. */
const GOOGLE_SHEETS = "google-sheet";

type Client = NonNullable<
  ExternalClientsQuery["externalClients"]["data"]
>[number];

/** What can be exported of the businesses picked, each as its own list reads it. */
const EXPORTS = [
  {
    key: "payments",
    name: "Bank payments",
    view: "transactions",
    tab: "bank-feed",
  },
  { key: "documents", name: "Documents", view: "documents", tab: null },
  {
    key: "journal-entries",
    name: "Journal entries",
    view: "journal-entries",
    tab: null,
  },
] as const;

/** A business drawn while the clients load: a typical one, each piece wrapped. */
const placeholderBusiness = (
  identifier: string,
  name: string,
  isUserEntity: boolean,
): Client => ({
  __typename: "ExternalClientObject",
  grant: {
    __typename: "ExternalGrantObject",
    id: identifier,
    identifier,
    createdAt: "",
    updatedAt: "",
    grantStatus: ExternalGrantStatusEnum.Active,
    invitedAt: null,
    isGiven: false,
    entityIdentifier: identifier,
    entityName: name,
    isOwnBooks: isUserEntity,
    sharedByIdentifier: "placeholder-client",
    sharedByName: "Client name",
    sharedByEmailAddress: "client@example.com",
    invitedEmailAddress: "you@firm.example.com",
    granteeName: "Your firm",
    granteeIdentifier: "placeholder-firm",
    roleName: "Accountant",
    roleIdentifier: "placeholder-role",
    rolePermissions: [PermissionEnum.Book, PermissionEnum.Reconcile],
  },
  roleName: "Bookkeeper",
  permissions: [PermissionEnum.Book, PermissionEnum.Reconcile],
  business: {
    __typename: "ExternalClientBusinessObject",
    entityIdentifier: identifier,
    name,
    isUserEntity,
    waitingCount: 0,
    missingDocumentCount: 0,
    latestPaymentOn: null,
    closedThrough: null,
  },
});

/** The client drawn while the clients load: their own books and a business. */
const PLACEHOLDER_CLIENT: Client[] = [
  placeholderBusiness("placeholder-own", "Client name", true),
  placeholderBusiness("placeholder-business", "Business name", false),
];

/**
 * The accountant's portal: every client - the person who shared their books -
 * and each of their businesses handed on to the reader, with how its books
 * stand and what the reader may do there; a client's businesses opened
 * together from here, and picked for an export to a CSV or a Google Sheet,
 * every business picked in one file, written again each day where it is kept
 * up to date - through the Google account the reader connected.
 */
export function ClientsView() {
  const { user } = useScope();
  const { data, loading } = useBackendQuery(Queries.EXTERNAL_CLIENTS_QUERY, {
    fetchPolicy: "cache-and-network",
  });
  const isLoading = loading && !data;
  const rows = data?.externalClients?.data ?? [];
  // A client is the person who shared: their businesses together, in the
  // order the server lists them.
  const clients = isLoading
    ? [PLACEHOLDER_CLIENT]
    : Object.values(_.groupBy(rows, (row) => row.grant.sharedByIdentifier));
  const openClientBooks = useOpenClientBooks();
  const { revoke, loading: isRevoking } = useRevokeExternalGrant();
  const [picked, setPicked] = useState<string[]>([]);
  const [exportKey, setExportKey] =
    useState<(typeof EXPORTS)[number]["key"]>("payments");
  const [isKeptUpToDate, setIsKeptUpToDate] = useState(false);
  const { mutate: requestBulkOperation, loading: isExporting } =
    useBackendMutation({ mutation: Mutations.REQUEST_BULK_OPERATION });
  // The Google account exports are written through: the newest the reader
  // connected that Google has not signed out.
  const { data: sheetsData, loading: sheetsLoading } = useBackendQuery(
    Queries.GOOGLE_SHEETS_CONNECTIONS_QUERY,
    { fetchPolicy: "cache-and-network" },
  );
  const sheets =
    (sheetsData?.emailConnections?.data ?? []).find(
      (connection) => connection.status === EmailConnectionStatusEnum.Connected,
    ) ?? null;
  const connectGoogleSheets = useConnectGoogleSheets();

  const onExport = (destination: BulkOperationDestinationEnum) => {
    const chosen = EXPORTS.find((one) => one.key === exportKey)!;
    const config = CONNECTION_VIEWS[chosen.view] as ConnectionViewConfig;
    const name = `${chosen.name} - ${picked.length} business${picked.length === 1 ? "" : "es"}`;
    void requestBulkOperation({
      variables: {
        input: {
          operation: BulkOperationEnum.Export,
          name,
          connection: config.queryKey,
          variables: connectionVariables(chosen.view, {
            tab: chosen.tab,
            filters: [],
            sortables: [],
            entityIdentifiers: picked,
          }),
          columns: toColumns(Objects[config.typename])
            .filter((column) => column.exportPath !== null)
            .map((column) => ({
              key: column.objectKey,
              label: column.label || _.startCase(column.objectKey),
              path: column.exportPath ?? column.objectKey,
            })),
          destination,
          ...(destination === BulkOperationDestinationEnum.GoogleSheet && sheets
            ? { googleSheetsConnectionIdentifier: sheets.identifier }
            : {}),
          isKeptUpToDate:
            destination === BulkOperationDestinationEnum.GoogleSheet &&
            isKeptUpToDate,
        },
      },
      refetchQueries: [Queries.INBOX_CONNECTION_QUERY],
      callbacks: {
        onSuccess: () =>
          State.getState().setSnackbarMessage(
            `Exporting ${name.toLowerCase()}. It will be in your notifications when it is ready.`,
          ),
      },
    });
  };

  return (
    <SettingsViewLayout
      title="Your clients"
      endDecorator={
        sheets ? (
          <Badge variant="secondary" data-testid="clients-google-sheets">
            <GoogleIcon size="1em" />
            {sheets.emailAddress
              ? `Google Sheets: ${sheets.emailAddress}`
              : "Google Sheets connected"}
          </Badge>
        ) : (
          <Skeleton
            loading={sheetsLoading && !sheetsData}
            variant="rectangular"
          >
            <Button
              variant="outline"
              size="sm"
              disabled={!user}
              data-testid="clients-connect-google-sheets"
              onClick={() => void connectGoogleSheets()}
            >
              <GoogleIcon />
              Connect Google Sheets
            </Button>
          </Skeleton>
        )
      }
    >
      <div className="flex flex-col gap-3 p-4">
        {picked.length > 0 && (
          <div
            data-testid="clients-export-bar"
            className="flex items-center gap-2 flex-wrap"
          >
            <Typography level="body-sm" className="mr-auto">
              {picked.length} business{picked.length === 1 ? "" : "es"} picked
            </Typography>
            <Select
              value={exportKey}
              onValueChange={(value) =>
                value && setExportKey(value as typeof exportKey)
              }
            >
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPORTS.map((one) => (
                  <SelectItem key={one.key} value={one.key}>
                    {one.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              data-testid="clients-export-csv"
              onClick={() => onExport(BulkOperationDestinationEnum.Csv)}
              disabled={isExporting}
            >
              {isExporting && <Spinner />}Export as CSV
            </Button>
            <Label className="flex items-center gap-2">
              <Checkbox
                checked={isKeptUpToDate}
                disabled={!sheets}
                onCheckedChange={(checked) =>
                  setIsKeptUpToDate(checked === true)
                }
              />
              Keep it up to date
            </Label>
            <Button
              variant="ghost"
              size="sm"
              disabled={isExporting || !sheets}
              data-testid="clients-export-sheet"
              onClick={() => onExport(BulkOperationDestinationEnum.GoogleSheet)}
            >
              {isExporting ? <Spinner /> : <GoogleIcon size="1em" />}Send to
              Google Sheets
            </Button>
          </div>
        )}

        {!isLoading && clients.length === 0 && (
          <Typography level="body-md" data-testid="clients-empty">
            No clients share their books with you yet. When one does, they're
            listed here.
          </Typography>
        )}

        {clients.map((books) => {
          const client = books[0].grant;
          const businesses = books.map((row) => row.business.entityIdentifier);
          const pickedHere = businesses.filter((identifier) =>
            picked.includes(identifier),
          );
          return (
            <SettingsViewCard
              key={client.sharedByIdentifier}
              icon={BriefcaseIcon}
              isLoading={isLoading}
              data-testid={`client-${client.sharedByIdentifier}`}
              title={client.sharedByName ?? client.sharedByEmailAddress}
              // Whose books, and for which firm.
              description={`${
                client.sharedByName ? `${client.sharedByEmailAddress} · ` : ""
              }${client.granteeName}`}
              actions={
                <>
                  <Label className="flex items-center gap-2">
                    <Checkbox
                      checked={
                        pickedHere.length > 0 &&
                        pickedHere.length < businesses.length
                          ? "indeterminate"
                          : businesses.length > 0 &&
                            pickedHere.length === businesses.length
                      }
                      disabled={isLoading}
                      onCheckedChange={(checked) =>
                        setPicked((was) =>
                          checked === true
                            ? _.uniq([...was, ...businesses])
                            : was.filter((one) => !businesses.includes(one)),
                        )
                      }
                    />
                    Pick all
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isLoading}
                    data-testid={`client-open-${client.sharedByIdentifier}`}
                    // Every business of theirs handed on, opened together.
                    onClick={() =>
                      openClientBooks(books.map((row) => row.grant.identifier))
                    }
                  >
                    Open books
                  </Button>
                </>
              }
            >
              {books.map(({ grant, business, roleName, permissions }) => (
                <div
                  key={business.entityIdentifier}
                  className="flex items-center gap-3 pl-3 pr-3 min-h-11 [border-top-width:1px] [border-top-style:solid] border-border"
                  data-testid={`client-business-${business.entityIdentifier}`}
                >
                  <Skeleton
                    loading={isLoading}
                    variant="rectangular"
                    className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
                  >
                    <Checkbox
                      checked={picked.includes(business.entityIdentifier)}
                      onCheckedChange={(checked) =>
                        setPicked((was) =>
                          checked === true
                            ? [...was, business.entityIdentifier]
                            : was.filter(
                                (one) => one !== business.entityIdentifier,
                              ),
                        )
                      }
                    />
                  </Skeleton>
                  <div className="flex-1 min-w-0">
                    <Typography level="body-sm" className="truncate">
                      <Skeleton loading={isLoading} variant="inline">
                        {business.isUserEntity
                          ? `${business.name} (own books)`
                          : business.name}
                      </Skeleton>
                    </Typography>
                    {/* What the reader does there: their role, held to the business's grant. */}
                    <Typography
                      level="body-xs"
                      className="truncate text-muted-foreground/80"
                    >
                      <Skeleton loading={isLoading} variant="inline">
                        {`${roleName}: ${describePermissions(permissions).toLowerCase()}`}
                      </Skeleton>
                    </Typography>
                  </div>
                  <Typography
                    level="body-xs"
                    className="text-muted-foreground/80"
                  >
                    <Skeleton loading={isLoading} variant="inline">
                      {business.waitingCount} waiting ·{" "}
                      {business.missingDocumentCount} missing a document
                    </Skeleton>
                  </Typography>
                  <Typography
                    level="body-xs"
                    className="truncate text-muted-foreground/80 w-40 text-right"
                  >
                    <Skeleton loading={isLoading} variant="inline">
                      {business.closedThrough
                        ? `Closed through ${business.closedThrough}`
                        : "No period closed"}
                    </Skeleton>
                  </Typography>
                  <Typography
                    level="body-xs"
                    className="truncate text-muted-foreground/80 w-37.5 text-right"
                  >
                    <Skeleton loading={isLoading} variant="inline">
                      {business.latestPaymentOn
                        ? `Last payment ${business.latestPaymentOn}`
                        : "No bank payments"}
                    </Skeleton>
                  </Typography>
                  <Skeleton
                    loading={isLoading}
                    variant="rectangular"
                    className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`client-leave-${grant.identifier}`}
                      onClick={() => void revoke(grant.identifier)}
                      disabled={isRevoking}
                      className="text-destructive hover:text-destructive"
                    >
                      {isRevoking && <Spinner />}Leave
                    </Button>
                  </Skeleton>
                </div>
              ))}
            </SettingsViewCard>
          );
        })}
      </div>
    </SettingsViewLayout>
  );
}

/**
 * Connecting the person's Google account for exports to Google Sheets: Nango's
 * sign-in window, then the connection kept as theirs - an ``EmailConnection``
 * whose provider is Google Sheets, given to no business. How it went is said
 * in the snackbar.
 */
function useConnectGoogleSheets() {
  const [createConnectSession] = useMutation(
    Mutations.CREATE_EMAIL_CONNECT_SESSION,
  );
  const [connectEmailAccount] = useMutation(Mutations.CONNECT_EMAIL_ACCOUNT, {
    refetchQueries: ["GoogleSheetsConnections"],
  });
  return async () => {
    const session = await createConnectSession({
      variables: { input: { providerConfigKey: GOOGLE_SHEETS } },
    });
    const token = session.data?.createEmailConnectSession?.data;
    if (!token) {
      State.getState().setSnackbarMessage(
        session.data?.createEmailConnectSession?.errors?.genericErrors?.[0]
          ?.message ?? "Couldn't start connecting Google Sheets. Try again.",
      );
      return;
    }
    const result = await new Nango({ connectSessionToken: token }).auth(
      GOOGLE_SHEETS,
    );
    const saved = await connectEmailAccount({
      variables: {
        input: {
          nangoConnectionId: result.connectionId,
          providerConfigKey: GOOGLE_SHEETS,
        },
      },
    });
    State.getState().setSnackbarMessage(
      saved.data?.connectEmailAccount?.data
        ? "Google Sheets connected."
        : (saved.data?.connectEmailAccount?.errors?.authorizationError
            ?.message ?? "Couldn't connect Google Sheets. Try again."),
    );
  };
}
